/**
 * EMZI NEXUS V6.4 - Live Studio V2
 * Step 8: SOA Drill (Live-specific questions)
 * AI Coaching in Bahasa Malaysia
 */

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { EMZI_SPACING, EMZI_RADIUS } from '../../../styles/emziCI';
import * as api from '../../../api/liveStudioV2';

const SOA_CONFIG = {
  passThreshold: 8.0,
  maxAttempts: 3,
  totalQuestions: 5
};

const Step8SOADrill = ({ sessionId, token, sessionData, onComplete, onBack, setError, setLoading }) => {
  const { language } = useLanguage();
  
  // State
  const [currentQ, setCurrentQ] = useState(0);
  const [attempt, setAttempt] = useState(1);
  const [answer, setAnswer] = useState('');
  const [answers, setAnswers] = useState(sessionData?.soaAnswers || {});
  const [lastEvaluation, setLastEvaluation] = useState(null);
  const [phase, setPhase] = useState('question'); // question, evaluating, feedback, ai_rescue, complete
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get questions from primed SOA
  const soaPlan = sessionData?.soaPlan;
  const questions = soaPlan?.blueprint || [];

  const getText = (key) => {
    const translations = {
      en: {
        title: 'SOA Drill',
        subtitle: '5 Live-Specific Questions',
        questionOf: 'Question',
        of: 'of',
        attemptLabel: 'Attempt',
        placeholder: 'Type your answer here...',
        submitBtn: 'Submit Answer',
        submittingBtn: 'Evaluating...',
        nextBtn: 'Next Question →',
        retryBtn: 'Try Again',
        aiRescueBtn: '✨ Accept AI Rescue',
        passedLabel: '✅ Passed',
        failedLabel: '❌ Need Improvement',
        scoreLabel: 'Score',
        coachFeedback: '🧑‍🏫 Coach Feedback',
        continueBtn: 'Continue to Script Generation →',
        summaryTitle: 'SOA Summary',
        avgScore: 'Average Score',
        passed: 'Passed',
        aiRescueUsed: 'AI Rescues Used'
      },
      ms: {
        title: 'SOA Drill',
        subtitle: '5 Soalan Khusus Live',
        questionOf: 'Soalan',
        of: 'daripada',
        attemptLabel: 'Percubaan',
        placeholder: 'Taip jawapan anda di sini...',
        submitBtn: 'Hantar Jawapan',
        submittingBtn: 'Menilai...',
        nextBtn: 'Soalan Seterusnya →',
        retryBtn: 'Cuba Lagi',
        aiRescueBtn: '✨ Terima AI Rescue',
        passedLabel: '✅ Lulus',
        failedLabel: '❌ Perlu Penambahbaikan',
        scoreLabel: 'Skor',
        coachFeedback: '🧑‍🏫 Maklum Balas Coach',
        continueBtn: 'Teruskan ke Script Generation →',
        summaryTitle: 'Ringkasan SOA',
        avgScore: 'Skor Purata',
        passed: 'Lulus',
        aiRescueUsed: 'AI Rescue Digunakan'
      },
      bm: {
        title: 'SOA Drill',
        subtitle: '5 Soalan Khusus Live',
        questionOf: 'Soalan',
        of: 'daripada',
        attemptLabel: 'Percubaan',
        placeholder: 'Taip jawapan anda di sini...',
        submitBtn: 'Hantar Jawapan',
        submittingBtn: 'Menilai...',
        nextBtn: 'Soalan Seterusnya →',
        retryBtn: 'Cuba Lagi',
        aiRescueBtn: '✨ Terima AI Rescue',
        passedLabel: '✅ Lulus',
        failedLabel: '❌ Perlu Penambahbaikan',
        scoreLabel: 'Skor',
        coachFeedback: '🧑‍🏫 Maklum Balas Coach',
        continueBtn: 'Teruskan ke Script Generation →',
        summaryTitle: 'Ringkasan SOA',
        avgScore: 'Skor Purata',
        passed: 'Lulus',
        aiRescueUsed: 'AI Rescue Digunakan'
      },
      urban: {
        title: 'SOA Drill',
        subtitle: '5 Live-Specific Questions',
        questionOf: 'Question',
        of: 'of',
        attemptLabel: 'Attempt',
        placeholder: 'Taip jawapan korang di sini...',
        submitBtn: 'Submit Answer',
        submittingBtn: 'Evaluating...',
        nextBtn: 'Next Question →',
        retryBtn: 'Try Again',
        aiRescueBtn: '✨ Accept AI Rescue',
        passedLabel: '✅ Passed',
        failedLabel: '❌ Need Improvement',
        scoreLabel: 'Score',
        coachFeedback: '🧑‍🏫 Coach Feedback',
        continueBtn: 'Continue ke Script Generation →',
        summaryTitle: 'SOA Summary',
        avgScore: 'Average Score',
        passed: 'Passed',
        aiRescueUsed: 'AI Rescues Used'
      }
    };
    return translations[language]?.[key] || translations.en[key];
  };

  // Get localized question text
  const getQuestionText = (q) => {
    if (!q) return '';
    switch (language) {
      case 'bm': return q.q_bm || q.q_en || q.q || '';
      case 'urban': return q.q_urban || q.q_en || q.q || '';
      default: return q.q_en || q.q || '';
    }
  };

  // Submit answer for evaluation
  const handleSubmit = async () => {
    if (!answer.trim()) {
      setError('Please enter an answer');
      return;
    }
    
    setIsSubmitting(true);
    setPhase('evaluating');
    setError(null);
    
    try {
      const result = await api.evaluateLiveSOA(token, sessionId, currentQ, answer, attempt);
      setLastEvaluation(result);
      setPhase('feedback');
      
      // Update answers
      const newAnswers = {
        ...answers,
        [currentQ]: {
          answer,
          score: result.score,
          passed: result.passed,
          attempt,
          aiRescueUsed: false
        }
      };
      setAnswers(newAnswers);
      
    } catch (err) {
      setError(err.message);
      setPhase('question');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle retry
  const handleRetry = () => {
    setAttempt(prev => prev + 1);
    setAnswer('');
    setLastEvaluation(null);
    setPhase('question');
  };

  // Accept AI Rescue
  const handleAIRescue = async () => {
    setIsSubmitting(true);
    
    try {
      await api.acceptLiveAIRescue(token, sessionId, currentQ, lastEvaluation?.ai_rescue_suggestion);
      
      // Update answers with AI rescue
      const newAnswers = {
        ...answers,
        [currentQ]: {
          answer: lastEvaluation?.ai_rescue_suggestion || '[AI Rescue]',
          score: 8.0,
          passed: true,
          attempt,
          aiRescueUsed: true
        }
      };
      setAnswers(newAnswers);
      setLastEvaluation({ ...lastEvaluation, passed: true, score: 8.0 });
      setPhase('feedback');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Move to next question
  const handleNext = () => {
    if (currentQ < SOA_CONFIG.totalQuestions - 1) {
      setCurrentQ(prev => prev + 1);
      setAttempt(1);
      setAnswer('');
      setLastEvaluation(null);
      setPhase('question');
    } else {
      // All questions done
      setPhase('complete');
    }
  };

  // Finalize and continue
  const handleContinue = async () => {
    setIsSubmitting(true);
    
    try {
      const result = await api.finalizeLiveSOA(token, sessionId, true);
      
      setError(null);
      onComplete({
        soaAnswers: Object.values(answers),
        soaSummary: result.summary
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate summary stats
  const getStats = () => {
    const answerList = Object.values(answers);
    const avgScore = answerList.length > 0 
      ? (answerList.reduce((sum, a) => sum + (a.score || 0), 0) / answerList.length).toFixed(1)
      : 0;
    const passed = answerList.filter(a => a.passed).length;
    const aiRescues = answerList.filter(a => a.aiRescueUsed).length;
    
    return { avgScore, passed, aiRescues, total: answerList.length };
  };

  const currentQuestion = questions[currentQ];
  const stats = getStats();

  // Complete phase UI
  if (phase === 'complete') {
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ marginBottom: EMZI_SPACING.xl, textAlign: 'center' }}>
          <h1 style={{ fontSize: '22px', fontWeight: '700', marginBottom: EMZI_SPACING.sm, color: '#fff' }}>
            🎉 SOA Drill Complete!
          </h1>
        </div>

        <Card style={{ marginBottom: EMZI_SPACING.lg, borderRadius: EMZI_RADIUS.md }}>
          <CardHeader>
            <CardTitle style={{ fontSize: '16px', fontWeight: '600' }}>
              📊 {getText('summaryTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
              <div style={{ padding: '16px', background: '#E8F4FF', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#0e67a2' }}>{stats.avgScore}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>{getText('avgScore')}</div>
              </div>
              <div style={{ padding: '16px', background: '#E6F9E6', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#28a745' }}>{stats.passed}/5</div>
                <div style={{ fontSize: '12px', color: '#666' }}>{getText('passed')}</div>
              </div>
              <div style={{ padding: '16px', background: '#FFF5E6', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#F89B29' }}>{stats.aiRescues}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>{getText('aiRescueUsed')}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <button
          onClick={handleContinue}
          disabled={isSubmitting}
          style={{
            width: '100%',
            padding: '16px',
            background: '#F7941E',
            border: 'none',
            borderRadius: '10px',
            color: '#fff',
            fontSize: '16px',
            fontWeight: '700',
            cursor: 'pointer'
          }}
        >
          {getText('continueBtn')}
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: EMZI_SPACING.lg, textAlign: 'center' }}>
        <h1 style={{ fontSize: '22px', fontWeight: '700', marginBottom: EMZI_SPACING.sm, color: '#fff' }}>
          🎯 {getText('title')}
        </h1>
        <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)' }}>{getText('subtitle')}</p>
      </div>

      {/* Progress */}
      <div style={{ marginBottom: EMZI_SPACING.lg }}>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          {[0, 1, 2, 3, 4].map(idx => (
            <div
              key={`item-${idx}`}
              style={{
                flex: 1,
                height: '8px',
                borderRadius: '4px',
                background: answers[idx]?.passed 
                  ? '#28a745' 
                  : answers[idx] 
                    ? '#dc3545' 
                    : idx === currentQ 
                      ? '#0e67a2' 
                      : '#E0E0E0'
              }}
            />
          ))}
        </div>
        <div style={{ fontSize: '13px', color: '#fff', textAlign: 'center' }}>
          {getText('questionOf')} {currentQ + 1} {getText('of')} 5 • {getText('attemptLabel')} {attempt}/{SOA_CONFIG.maxAttempts}
        </div>
      </div>

      {/* Question Card */}
      <Card style={{ marginBottom: EMZI_SPACING.lg, borderRadius: EMZI_RADIUS.md }}>
        <CardHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ 
              padding: '4px 12px', 
              background: '#667eea', 
              color: '#fff', 
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {currentQuestion?.domain?.replace('_', ' ').toUpperCase()}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          <p style={{ fontSize: '16px', fontWeight: '500', color: '#333', lineHeight: 1.6, marginBottom: '20px' }}>
            {getQuestionText(currentQuestion)}
          </p>

          {/* Answer Input */}
          {phase === 'question' && (
            <>
              <textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder={getText('placeholder')}
                rows={5}
                style={{
                  width: '100%',
                  padding: '14px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '10px',
                  fontSize: '14px',
                  resize: 'vertical',
                  marginBottom: '16px'
                }}
              />
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !answer.trim()}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: '#0e67a2',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {getText('submitBtn')}
              </button>
            </>
          )}

          {/* Evaluating */}
          {phase === 'evaluating' && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '32px', marginBottom: '16px' }}>🤔</div>
              <p style={{ color: '#666' }}>{getText('submittingBtn')}</p>
            </div>
          )}

          {/* Feedback */}
          {phase === 'feedback' && lastEvaluation && (
            <div>
              {/* Score Badge */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                marginBottom: '20px' 
              }}>
                <div style={{
                  padding: '12px 24px',
                  background: lastEvaluation.passed ? '#E6F9E6' : '#FEE',
                  borderRadius: '20px',
                  border: `2px solid ${lastEvaluation.passed ? '#28a745' : '#dc3545'}`
                }}>
                  <span style={{ fontSize: '18px', fontWeight: '700', color: lastEvaluation.passed ? '#28a745' : '#dc3545' }}>
                    {lastEvaluation.passed ? getText('passedLabel') : getText('failedLabel')}
                  </span>
                  <span style={{ marginLeft: '12px', fontSize: '16px', fontWeight: '600' }}>
                    {getText('scoreLabel')}: {lastEvaluation.score}/10
                  </span>
                </div>
              </div>

              {/* Coach Feedback */}
              <div style={{
                padding: '16px',
                background: '#f8f9fa',
                borderRadius: '10px',
                borderLeft: '4px solid #667eea',
                marginBottom: '20px'
              }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#667eea', marginBottom: '8px' }}>
                  {getText('coachFeedback')}
                </div>
                <p style={{ fontSize: '14px', color: '#333', lineHeight: 1.5, margin: 0 }}>
                  {lastEvaluation.feedback}
                </p>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px' }}>
                {!lastEvaluation.passed && attempt < SOA_CONFIG.maxAttempts && (
                  <button
                    onClick={handleRetry}
                    style={{
                      flex: 1,
                      padding: '14px',
                      background: '#fff',
                      border: '2px solid #0e67a2',
                      borderRadius: '10px',
                      color: '#0e67a2',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    {getText('retryBtn')}
                  </button>
                )}
                
                {!lastEvaluation.passed && lastEvaluation.ai_rescue_available && (
                  <button
                    onClick={handleAIRescue}
                    disabled={isSubmitting}
                    style={{
                      flex: 1,
                      padding: '14px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    {getText('aiRescueBtn')}
                  </button>
                )}
                
                {(lastEvaluation.passed || attempt >= SOA_CONFIG.maxAttempts) && (
                  <button
                    onClick={handleNext}
                    style={{
                      flex: 1,
                      padding: '14px',
                      background: '#F7941E',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    {currentQ < 4 ? getText('nextBtn') : getText('continueBtn')}
                  </button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Back Button */}
      <button
        onClick={onBack}
        style={{
          width: '100%',
          padding: '14px',
          background: 'transparent',
          border: '2px solid rgba(255,255,255,0.5)',
          borderRadius: '10px',
          color: '#fff',
          fontSize: '15px',
          fontWeight: '600',
          cursor: 'pointer'
        }}
      >
        ← Back to Review
      </button>
    </div>
  );
};

export default Step8SOADrill;
